import React, { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../../utils/api";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";
import * as XLSX from 'xlsx';

// Estructura de columnas basada en la imagen compartida
export default function Productos() {
  const { empresaSeleccionada, esConexAdmin } = useEmpresaSelector();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevo, setNuevo] = useState({
    codigo: "",
    nombre: "",
    distribuidor: "",
    unidad: "unidad",
    costoArticulo: "",
    iva: "",
    precioUnitario: "",
    cantidadEnStock: "",
    salida: "0",
    precioStockTotal: "",
    precioStockFinal: "",
  });

  // Calcular precio unitario cuando cambian costoArticulo o iva
  useEffect(() => {
    const costo = parseFloat(nuevo.costoArticulo) || 0;
    const iva = parseFloat(nuevo.iva) || 0;
    const precioUnitario = costo * (1 + iva / 100); // Precio unitario = costo * (1 + IVA/100)
    setNuevo(prev => ({ ...prev, precioUnitario: precioUnitario.toString() }));
  }, [nuevo.costoArticulo, nuevo.iva]);

  // Calcular precios de stock cuando cambian precioUnitario, cantidadEnStock o salida
  useEffect(() => {
    const precioUnitario = parseFloat(nuevo.precioUnitario) || 0;
    const cantidadEnStock = parseInt(nuevo.cantidadEnStock) || 0;
    const salida = parseInt(nuevo.salida) || 0;
    const precioStockTotal = Math.round(precioUnitario * cantidadEnStock * 100) / 100; // Redondear a 2 decimales
    const stockFinal = Math.max(cantidadEnStock - salida, 0);
    const precioStockFinal = Math.round(precioUnitario * stockFinal * 100) / 100; // Redondear a 2 decimales

    setNuevo(prev => ({
      ...prev,
      precioStockTotal: precioStockTotal.toString(),
      precioStockFinal: precioStockFinal.toString()
    }));
  }, [nuevo.precioUnitario, nuevo.cantidadEnStock, nuevo.salida]);

  const [fechaFiltro, setFechaFiltro] = useState(new Date().toISOString().split("T")[0]);
  const [ventas, setVentas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    codigo: "",
    nombre: "",
    distribuidor: "",
    unidad: "unidad",
    costoArticulo: "",
    iva: "",
    precioUnitario: "",
    cantidadEnStock: "",
    salida: "",
    precioStockTotal: "",
    precioStockFinal: "",
  });

  // Calcular precio unitario en editForm cuando cambian costoArticulo o iva
  useEffect(() => {
    const costo = parseFloat(editForm.costoArticulo) || 0;
    const iva = parseFloat(editForm.iva) || 0;
    const precioUnitario = costo * (1 + iva / 100); // Precio unitario = costo * (1 + IVA/100)
    setEditForm(prev => ({ ...prev, precioUnitario: precioUnitario.toString() }));
  }, [editForm.costoArticulo, editForm.iva]);

  // Cargar items de inventario desde el backend
  useEffect(() => {
    const cargarInventario = async () => {
      try {
        setLoading(true);

        if (!empresaSeleccionada) {
          setItems([]);
          return;
        }

        let endpoint = '/inventario';
        const params = [];
        
        if (esConexAdmin && empresaSeleccionada.id !== 'all') {
          params.push(`empresaId=${empresaSeleccionada.id}`);
        }
        
        // Ya no enviamos el filtro de fecha al backend para el inventario,
        // para que siempre aparezcan todos los productos sin importar la fecha.
        
        if (params.length > 0) {
          endpoint += `?${params.join('&')}`;
        }

        const response = await apiRequest(endpoint);
        if (response.ok) {
          const data = await response.json();
          setItems(Array.isArray(data) ? data : []);
        } else {
          setItems([]);
        }
      } catch (error) {
        console.error('Error cargando inventario:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    cargarInventario();
  }, [empresaSeleccionada, esConexAdmin]);

  // Cargar ventas para cálculos
  useEffect(() => {
    if (empresaSeleccionada?.id) {
      const qs = esConexAdmin && empresaSeleccionada.id !== 'all' ? `?empresaId=${empresaSeleccionada.id}` : '';
      apiRequest(`/ingresos${qs}`)
        .then((r) => r.json())
        .then((data) => setVentas(Array.isArray(data) ? data : []))
        .catch(() => setVentas([]));
    } else {
      setVentas([]);
    }
  }, [empresaSeleccionada?.id, esConexAdmin]);

  const ventasPorProductoHoy = useMemo(() => {
    const mapa = {};
    ventas.filter(v => v.fecha === fechaFiltro).forEach(v => {
      mapa[v.producto] = (mapa[v.producto] || 0) + (parseFloat(v.cantidad) || 0); // suma de cantidades vendidas
    });
    return mapa;
  }, [ventas, fechaFiltro]);

  // Calcular precios de stock en editForm cuando cambian precioUnitario, cantidadEnStock o salida
  useEffect(() => {
    const precioUnitario = parseFloat(editForm.precioUnitario) || 0;
    const cantidadEnStock = parseInt(editForm.cantidadEnStock) || 0;
    const salida = parseInt(editForm.salida) || 0;
    const precioStockTotal = Math.round(precioUnitario * cantidadEnStock * 100) / 100; // Redondear a 2 decimales
    // Stock final = stock total - salida manual
    const stockFinal = Math.max(cantidadEnStock - salida, 0);
    const precioStockFinal = Math.round(precioUnitario * stockFinal * 100) / 100; // Redondear a 2 decimales

    setEditForm(prev => ({
      ...prev,
      precioStockTotal: precioStockTotal.toString(),
      precioStockFinal: precioStockFinal.toString()
    }));
  }, [editForm.precioUnitario, editForm.cantidadEnStock, editForm.salida]);

  const enriquecidos = useMemo(() => {
    return items.map(it => {
      const vendido = ventasPorProductoHoy[it.nombre] || 0;
      const cantidadEnStock = it.cantidadEnStock || 0;
      // Usar salida manual si existe, sino usar ventas calculadas
      const salida = it.salida !== undefined ? it.salida : vendido;
      const stockFinalDia = Math.max(cantidadEnStock - salida, 0);
      const costoArticulo = it.costoArticulo || 0;
      const iva = it.iva || 0;
      const precioUnitario = costoArticulo * (1 + iva / 100); // Precio unitario = costo * (1 + IVA/100)
      const precioStockTotal = Math.round(precioUnitario * cantidadEnStock * 100) / 100; // Redondear a 2 decimales
      const precioStockFinal = Math.round(precioUnitario * stockFinalDia * 100) / 100; // Redondear a 2 decimales

      return {
        ...it,
        vendido,
        salida,
        cantidadEnStock,
        stockFinalDia,
        costoArticulo,
        iva,
        precioUnitario,
        precioStockTotal,
        precioStockFinal,
        // Legacy fields for compatibility
        valorActivo: precioStockTotal,
        valorFinalDia: precioStockFinal
      };
    });
  }, [items, ventasPorProductoHoy]);

  const totalActivos = useMemo(() => {
    return enriquecidos.reduce((acc, it) => acc + (parseFloat(it.valorActivo || 0)), 0);
  }, [enriquecidos]);

  const totalValorFinal = useMemo(() => {
    return enriquecidos.reduce((acc, it) => acc + (parseFloat(it.precioStockFinal || 0)), 0);
  }, [enriquecidos]);

  // Eliminado resumen de "Vendido (día)" para simplificar UI

  const totalItems = useMemo(() => enriquecidos.length, [enriquecidos]);

  const agregarItem = async () => {
    if (!nuevo.nombre || !nuevo.costoArticulo || !nuevo.cantidadEnStock) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      const itemAEnviar = {
        codigo: nuevo.codigo,
        nombre: nuevo.nombre,
        distribuidor: nuevo.distribuidor,
        unidad: nuevo.unidad,
        costoArticulo: parseFloat(nuevo.costoArticulo),
        iva: parseFloat(nuevo.iva) || 0,
        precioUnitario: parseFloat(nuevo.precioUnitario),
        cantidadEnStock: parseInt(nuevo.cantidadEnStock),
        salida: parseInt(nuevo.salida) || 0,
        precioStockTotal: parseFloat(nuevo.precioStockTotal),
        precioStockFinal: parseFloat(nuevo.precioStockFinal),
        empresaId: empresaSeleccionada?.id || null,
      };

      const response = await apiRequest('/inventario', {
        method: 'POST',
        body: JSON.stringify(itemAEnviar),
      });

      if (response.ok) {
        const itemCreado = await response.json();
        setItems(prev => [...prev, itemCreado]);
        setNuevo({ codigo: "", nombre: "", distribuidor: "", unidad: "unidad", costoArticulo: "", iva: "", precioUnitario: "", cantidadEnStock: "", salida: "0", precioStockTotal: "", precioStockFinal: "" });
        alert('Producto agregado exitosamente');
      } else {
        const error = await response.json();
        alert(`Error al agregar producto: ${error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error agregando producto:', error);
      alert('Error al agregar producto');
    }
  };

  const eliminarItem = async (id) => {
    if (!confirm('¿Está seguro de eliminar este producto?')) return;

    // Eliminar localmente inmediatamente
    setItems(prev => prev.filter(item => item.id !== id));
    alert('Producto eliminado exitosamente');

    try {
      apiRequest(`/inventario/${id}`, {
        method: 'DELETE',
      }); // Fire and forget
    } catch (error) {
      console.error('Error eliminando producto:', error);
    }
  };

  const iniciarEdicion = (item) => {
    setEditingId(item.id);
    setEditForm({
      codigo: item.codigo || "",
      nombre: item.nombre || "",
      distribuidor: item.distribuidor || "",
      unidad: item.unidad || "unidad",
      costoArticulo: item.costoArticulo || "",
      iva: item.iva || "",
      precioUnitario: item.precioUnitario || "",
      cantidadEnStock: item.cantidadEnStock || "",
      salida: item.salida || item.vendido || "", // Usar salida almacenada, o vendido como fallback
      precioStockTotal: item.precioStockTotal || "",
      precioStockFinal: item.precioStockFinal || "",
    });
  };


  const guardarEdicion = async () => {
    if (!editForm.nombre || !editForm.costoArticulo || !editForm.cantidadEnStock) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      const itemActualizado = {
        codigo: editForm.codigo,
        nombre: editForm.nombre,
        distribuidor: editForm.distribuidor,
        unidad: editForm.unidad,
        costoArticulo: parseFloat(editForm.costoArticulo),
        iva: parseFloat(editForm.iva) || 0,
        precioUnitario: parseFloat(editForm.precioUnitario),
        cantidadEnStock: parseInt(editForm.cantidadEnStock),
        salida: parseInt(editForm.salida) || 0,
        precioStockTotal: parseFloat(editForm.precioStockTotal),
        precioStockFinal: parseFloat(editForm.precioStockFinal),
      };

      const response = await apiRequest(`/inventario/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify(itemActualizado),
      });

      if (response.ok) {
        const item = await response.json();
        setItems(prev => prev.map(it => it.id === editingId ? item : it));
        setEditingId(null);
        setEditForm({
          codigo: "",
          nombre: "",
          distribuidor: "",
          unidad: "unidad",
          costoArticulo: "",
          iva: "",
          precioUnitario: "",
          cantidadEnStock: "",
          salida: "",
          precioStockTotal: "",
          precioStockFinal: "",
        });
        alert('Producto actualizado exitosamente');
      } else {
        const error = await response.json();
        alert(`Error al actualizar producto: ${error.error || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error actualizando producto:', error);
      alert('Error al actualizar producto');
    }
  };

  const descargarExcel = () => {
    const rows = enriquecidos.map(it => ({
      'N° de artículo': it.codigo,
      'Nombre del artículo': it.nombre,
      Distribuidor: it.distribuidor,
      Unidad: it.unidad,
      'Stock total': it.cantidadEnStock,
      Salida: it.salida,
      'Stock final': it.stockFinalDia,
      'Costo unitario': it.costoArticulo,
      IVA: `${parseFloat(it.iva || 0).toFixed(2)}%`,
      'Precio unitario': parseFloat(it.precioUnitario || 0).toFixed(3),
      'Precio stock total': parseFloat(it.precioStockTotal || 0).toFixed(3),
      'Precio stock final': parseFloat(it.precioStockFinal || 0).toFixed(2),
      FechaVentas: fechaFiltro,
    }));
    
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
    
    // Generate filename with current date
    const fechaDescarga = new Date().toISOString().split('T')[0];
    const nombreArchivo = `inventario_${fechaFiltro}_descargado_${fechaDescarga}.xlsx`;
    
    // Save the file
    XLSX.writeFile(wb, nombreArchivo);
  };

const cancelarEdicion = () => {
  setEditingId(null);
  setEditForm({
    codigo: "",
    nombre: "",
    distribuidor: "",
    unidad: "unidad",
    costoArticulo: "",
    iva: "",
    precioUnitario: "",
    cantidadEnStock: "",
    salida: "",
    precioStockTotal: "",
    precioStockFinal: "",
  });
};



return (
  <div className="box" style={{ padding: '1.5rem' }}>
    <div className="is-flex is-justify-content-space-between is-align-items-center is-flex-wrap-wrap mb-3">
      <h2 className="title is-5">Productos</h2>
      <div className="is-flex is-align-items-center">
        <button className="button is-gold" onClick={descargarExcel}>
          <span className="icon">
            <i className="fas fa-file-excel"></i>
          </span>
          <span>Excel</span>
        </button>
      </div>
    </div>

    {loading ? (
      <div className="has-text-centered">
        <p>Cargando productos...</p>
      </div>
    ) : !empresaSeleccionada && esConexAdmin ? (
      <div className="has-text-centered">
        <p className="has-text-warning">
          Selecciona una empresa para ver y gestionar el inventario
        </p>
      </div>
    ) : !empresaSeleccionada && !esConexAdmin ? (
      <div className="has-text-centered">
        <p className="has-text-warning">
          No tienes una empresa asignada. Contacta al administrador para que te asigne una empresa.
        </p>
      </div>
    ) : (
      <div className="table-container">
        <table className="table is-fullwidth is-bordered is-striped is-size-5" style={{ marginBottom: '0.5rem' }}>
          <thead>
            <tr className="has-background-warning">
              <th>N° de artículo</th>
              <th>Nombre del artículo</th>
              <th>Distribuidor</th>
              <th>Unidad</th>
              <th>Stock total</th>
              <th>Salida</th>
              <th>Stock final</th>
              <th>Costo unitario</th>
              <th>IVA</th>
              <th>Precio unitario</th>
              <th>Precio stock total</th>
              <th>Precio stock final</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {enriquecidos.map((it) => {
              const isEditing = editingId === it.id;
              return (
                <tr key={it.id}>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        value={editForm.codigo}
                        onChange={(e) => setEditForm({...editForm, codigo: e.target.value})}
                      />
                    ) : (
                      it.codigo || '-'
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        value={editForm.nombre}
                        onChange={(e) => setEditForm({...editForm, nombre: e.target.value})}
                      />
                    ) : (
                      it.nombre
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        value={editForm.distribuidor}
                        onChange={(e) => setEditForm({...editForm, distribuidor: e.target.value})}
                      />
                    ) : (
                      it.distribuidor || '-'
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        value={editForm.unidad}
                        onChange={(e) => setEditForm({...editForm, unidad: e.target.value})}
                      />
                    ) : (
                      it.unidad || 'unidad'
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        value={editForm.cantidadEnStock}
                        onChange={(e) => setEditForm({...editForm, cantidadEnStock: e.target.value})}
                      />
                    ) : (
                      it.cantidadEnStock || 0
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        value={editForm.salida}
                        onChange={(e) => setEditForm({...editForm, salida: e.target.value})}
                      />
                    ) : (
                      it.salida
                    )}
                  </td>
                  <td>{it.stockFinalDia}</td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={editForm.costoArticulo}
                        onChange={(e) => setEditForm({...editForm, costoArticulo: e.target.value})}
                      />
                    ) : (
                      `$${parseFloat(it.costoArticulo || 0).toFixed(2)}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={editForm.iva}
                        onChange={(e) => setEditForm({...editForm, iva: e.target.value})}
                      />
                    ) : (
                      `$${parseFloat(it.iva || 0).toFixed(2)}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={editForm.precioUnitario}
                        readOnly
                        style={{ backgroundColor: '#f5f5f5' }}
                      />
                    ) : (
                      `$${parseFloat(it.precioUnitario || 0).toFixed(2)}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={editForm.precioStockTotal}
                        readOnly
                        style={{ backgroundColor: '#f5f5f5' }}
                      />
                    ) : (
                      `$${parseFloat(it.precioStockTotal || 0).toFixed(2)}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={editForm.precioStockFinal}
                        readOnly
                        style={{ backgroundColor: '#f5f5f5' }}
                      />
                    ) : (
                      `$${parseFloat(it.precioStockFinal || 0).toFixed(2)}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <div className="buttons">
                        <button className="button is-gold is-small mr-1" onClick={guardarEdicion} title="Guardar">
                          <span className="icon"><i className="fas fa-save"></i></span>
                        </button>
                        <button className="button is-light is-small" onClick={cancelarEdicion} title="Cancelar">
                          <span className="icon"><i className="fas fa-times"></i></span>
                        </button>
                      </div>
                    ) : (
                      <div className="buttons">
                        <button className="button is-gold is-small mr-1" onClick={() => iniciarEdicion(it)} title="Editar">
                          <span className="icon"><i className="fas fa-edit"></i></span>
                        </button>
                        <button className="button is-danger is-small" onClick={() => eliminarItem(it.id)} title="Eliminar">
                          <span className="icon"><i className="fas fa-trash"></i></span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td>
                <input
                  className="input"
                  value={nuevo.codigo}
                  onChange={(e) => setNuevo({...nuevo, codigo: e.target.value})}
                  placeholder="Código"
                />
              </td>
              <td>
                <input
                  className="input"
                  value={nuevo.nombre}
                  onChange={(e) => setNuevo({...nuevo, nombre: e.target.value})}
                  placeholder="Nombre"
                />
              </td>
              <td>
                <input
                  className="input"
                  value={nuevo.distribuidor}
                  onChange={(e) => setNuevo({...nuevo, distribuidor: e.target.value})}
                  placeholder="Distribuidor"
                />
              </td>
              <td>
                <input
                  className="input"
                  value={nuevo.unidad}
                  onChange={(e) => setNuevo({...nuevo, unidad: e.target.value})}
                  placeholder="Unidad"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  value={nuevo.cantidadEnStock}
                  onChange={(e) => setNuevo({...nuevo, cantidadEnStock: e.target.value})}
                  placeholder="0"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  value={nuevo.salida}
                  onChange={(e) => setNuevo({...nuevo, salida: e.target.value})}
                  placeholder="0"
                />
              </td>
              <td></td>
              <td>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={nuevo.costoArticulo}
                  onChange={(e) => setNuevo({...nuevo, costoArticulo: e.target.value})}
                  placeholder="$0.00"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={nuevo.iva}
                  onChange={(e) => setNuevo({...nuevo, iva: e.target.value})}
                  placeholder="0.00"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={nuevo.precioUnitario}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  placeholder="$0.00"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={nuevo.precioStockTotal}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  placeholder="$0.00"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  value={nuevo.precioStockFinal}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  placeholder="$0.00"
                />
              </td>
              <td>
                <button className="button is-gold is-small" onClick={agregarItem}>Agregar</button>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={10} className="has-text-right has-text-weight-semibold">TOTALES</td>
              <td className="has-text-weight-semibold">${totalActivos.toFixed(2)}</td>
              <td className="has-text-weight-semibold">${totalValorFinal.toFixed(2)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        
        {/* Resumen estilo cinta azul - Movido al final */}
        <div className="box mt-4" style={{ background: '#eaf3ff' }}>
          <div className="is-flex is-justify-content-space-around is-flex-wrap-wrap has-text-link">
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Items: {totalItems}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Valor Activo: ${totalActivos.toFixed(2)}</p>
            <p className="has-text-weight-semibold" style={{ fontSize: '1.05rem' }}>Precio Stock Final Total: ${totalValorFinal.toFixed(2)}</p>
          </div>
        </div>
        
        <p className="is-size-7 has-text-grey mt-3">
          Los datos de inventario se guardan en el backend por empresa. Las ventas se calculan desde los ingresos registrados.
        </p>
      </div>
    )}
  </div>
);
}
